import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Home, Replay } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

const ReturnsRefundsPage = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "Initiate a lot return",
      content: [
        "Physical inspection to be done within 4 days of booking a lot.",
        "If material is not approved during inspection 100% refund will be done within 3 days strictly.",
        "Failing to which mall of stones is liable to pay 18%/ annum interest on delayed days."
      ]
    },
     {
      title: "Return Eligibility",
      content: [
        "Products can be returned if they are damaged, defective, or incorrect at the time of delivery.",
        "Items must be unused and in their original condition and packaging.",
        "Return requests must be initiated within 7–14 days of delivery (as applicable).",
        "Customized, cut-to-size, or made-to-order stone products are non-returnable."
      ]
    },
    {
      title: "How to Initiate a Return",
      content: [
        "Email our support team with your order ID and reason for return.",
        "Attach clear images or videos in case of damaged or defective items.",
        "Our team will review and confirm the return request within 2–3 business days."
      ]
    },
    {
      title: "Return Shipping",
      content: [
        "If the return is due to damage, defect, or incorrect delivery, return shipping will be arranged by us.",
        "For all other eligible returns, shipping costs may be borne by the customer.",
        "Products must be securely packed to prevent transit damage."
      ]
    },
    {
      title: "Refund Policy",
      content: [
        "Refunds are processed only after the returned item passes quality inspection.",
        "Approved refunds are initiated within 5–10 business days.",
        "Refunds are credited to the original payment method used at checkout.",
        "Delivery and handling charges are non-refundable."
      ]
    },
    {
      title: "Exchange Policy",
      content: [
        "Exchanges are applicable only for damaged, defective, or incorrect items.",
        "All exchanges are subject to stock availability.",
        "In case an exchange is unavailable, a refund will be processed as per policy."
      ]
    },
    {
      title: "Order Cancellation",
      content: [
        "Orders can be cancelled before dispatch without any charges.",
        "Once dispatched, cancellations are treated as return requests.",
        "Custom and made-to-measure orders cannot be cancelled once processing begins."
      ]
    },
    {
      title: "Need Help?",
      content: [
        "For any return, refund, or cancellation-related queries, please reach out to our support team.",
        "We are committed to ensuring a smooth and transparent post-purchase experience."
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Returns & Refunds</Typography>
        </Breadcrumbs>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Replay sx={{ fontSize: 60, mb: 2, opacity: 0.9, color: 'white' }} />
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              Returns & Refunds
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', mt: 1, color: 'white' }}
            >
              Transparent policies designed to protect your purchase and ensure peace of mind.
            </Typography>
          </Paper>
        </motion.div>

        {/* Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 * index }}
          >
            <Paper sx={{ p: 4, mb: 3 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: 'primary.main', mb: 2 }}
              >
                {section.title}
              </Typography>
              <List>
                {section.content.map((item, i) => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </motion.div>
        ))}
      </Container>
    </Box>
  );
};

export default ReturnsRefundsPage;
